import React, { useMemo, useEffect } from 'react';
import { Box, Divider, Stack, Alert } from '@mui/material';
import { UseFormReturn } from 'react-hook-form';

import { FormCheckboxField } from 'components/form/FormCheckboxField';
import { FormSelectField } from 'components/form/FormSelectField';
import { FormTextField } from 'components/form/FormTextField';
import { FlexBox } from 'components/FlexBox';
import { PrimaryButton } from 'components/PrimaryButton';
import { CancelButton } from 'components/CancelButton';
import { DeleteButton } from 'components/DeleteButton';

import useMemoTranslation from '../../../hooks/UseMemoTranslation';
import { PublicPermission, GridLayout, GridFilter } from '../../../data/GridViewData';

export interface GridViewFormInput {
  name: string;
  isPublic: boolean;
  publicPermission: PublicPermission;
  updateWithCurrent?: boolean;
}

export interface GridViewFormProps {
  recordType: 'layout' | 'filter';
  form: UseFormReturn<GridViewFormInput>;
  selectedRecord?: GridLayout | GridFilter | null;
  onSave?: (data: GridViewFormInput) => void;
  onUpdate?: (data: GridViewFormInput) => void;
  onCancel?: () => void;
  onDelete?: () => void;
  isLoading?: boolean;
  showForm?: boolean;
  scopeLabel?: string;
}

export const defaultFormValues: GridViewFormInput = {
  name: '',
  isPublic: false,
  publicPermission: 'Read' as PublicPermission,
  updateWithCurrent: true,
};

const PERMISSION_OPTIONS: PublicPermission[] = ['Read', 'Write', 'Admin'];

const formatPermissionForDisplay = (value: string): string => {
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
};

export const GridViewForm = ({
  recordType,
  form,
  selectedRecord,
  onSave,
  onUpdate,
  onCancel,
  onDelete,
  isLoading = false,
  showForm = false,
  scopeLabel,
}: GridViewFormProps) => {
  const { t } = useMemoTranslation();
  const { control, watch, trigger, reset } = form;
  const isPublic = watch('isPublic');

  const formMode = selectedRecord ? 'edit' : 'create';

  useEffect(() => {
    if (selectedRecord) {
      const formData = {
        name: selectedRecord.name,
        isPublic: selectedRecord.isPublic,
        publicPermission: selectedRecord.publicPermission || 'Read',
        updateWithCurrent: true,
      };
      reset(formData);

      setTimeout(() => {
        trigger('name');
      }, 0);
    } else {
      reset(defaultFormValues);
    }
  }, [selectedRecord, reset, trigger]);

  const handleFormSubmit = (data: GridViewFormInput) => {
    if (formMode === 'create') {
      onSave?.(data);
    } else {
      onUpdate?.(data);
    }
  };

  const disablePermissions = useMemo(() => {
    return formMode === 'edit' && !onDelete;
  }, [formMode, onDelete]);

  const canUpdate = useMemo(() => {
    return formMode === 'edit' && selectedRecord?.canWrite;
  }, [formMode, selectedRecord]);

  return (
    <>
      <Divider sx={{ mt: 1, mb: 1 }} />
      <Box component='form' onSubmit={form.handleSubmit(handleFormSubmit)} sx={{ flexGrow: 1, mt: 1.5 }}>
        {showForm && (
          <>
            <FormTextField name='name' control={control} label={t('name')} trigger={trigger} required sx={{ mb: 1 }} />
            <FlexBox justifyContent='space-between' width='100%'>
              <FormCheckboxField
                name='isPublic'
                control={control}
                label={t('isPublic')}
                trigger={trigger}
                sx={{ justifyContent: 'space-between', marginLeft: 0, mb: 1, mt: 1 }}
                disabled={disablePermissions}
              />
              <FormSelectField
                name='publicPermission'
                control={control}
                label={t('publicPermission')}
                trigger={trigger}
                options={PERMISSION_OPTIONS}
                required={isPublic}
                sx={{ mb: 1, mt: 1, width: '50%' }}
                inputSx={{ mt: 0 }}
                disabled={!isPublic || disablePermissions}
                additionalProps={{
                  getOptionLabel: (option: string) => formatPermissionForDisplay(option),
                }}
              />
            </FlexBox>
            {scopeLabel && formMode === 'create' && (
              <Alert severity='info' sx={{ mb: 1 }}>
                This {recordType} will be saved for: <strong>{scopeLabel}</strong>
              </Alert>
            )}
          </>
        )}
        {canUpdate && (
          <FormCheckboxField
            name='updateWithCurrent'
            control={control}
            label={t(recordType === 'layout' ? 'updateWithCurrentLayout' : 'updateWithCurrentFilter')}
            trigger={trigger}
            sx={{ justifyContent: 'space-between', marginLeft: 0 }}
          />
        )}
        {canUpdate || formMode === 'create' ? (
          <Stack justifyContent='flex-end' direction='row' spacing={1}>
            <CancelButton onClick={onCancel} disabled={isLoading} />
            {formMode === 'edit' && onDelete && <DeleteButton onClick={onDelete} disabled={isLoading} />}
            <PrimaryButton type='submit' disabled={isLoading}>
              Save
            </PrimaryButton>
          </Stack>
        ) : null}
      </Box>
    </>
  );
};
